import React, { useEffect, useMemo, useRef } from 'react'

import { Box, Color, Display, FlexDirection, ZIndex } from '@gousto-internal/citrus-react'

import Loading from 'routes/Menu/Loading'

import type { MenuQueryParams } from '../../context/menuQueryContext'
import { BrowseScreenWrapper } from './BrowseScreenWrapper'
import { CollectionScreenWrapper } from './CollectionScreenWrapper'
import { MenuExperienceCategoryNav } from './MenuExperienceCategoryNav'
import { MenuBrandingContext, MenuBrandingProvider, getMenuBranding } from './menuBrandingContext'
import { MenuExperienceNavigationProvider } from './menuExperienceNavigationContext'
import { useMenuExperienceNavigation } from './useMenuExperienceNavigation'

type MenuExperiencePageProps = {
  location: {
    pathname?: string
    query: MenuQueryParams & Record<string, unknown> & { collection?: string }
    [key: string]: unknown
  }
  [key: string]: unknown
}

export const MenuExperiencePage = ({ location }: MenuExperiencePageProps) => {
  const hasMountedRef = useRef(false)
  const { data, error, links, selectCategory } = useMenuExperienceNavigation(
    location.query,
    location.pathname,
  )
  const navigationActions = useMemo(() => ({ selectCategory }), [selectCategory])
  const isCategoryNavVisible = links.some((link) => link.selected)

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true

      return
    }

    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [location.query.collection])

  const hasInitialLoadError = !data && error

  if (hasInitialLoadError) {
    throw error
  }

  if (!data) {
    return (
      <Box data-testid="loading">
        <Loading loading />
      </Box>
    )
  }

  const ScreenWrapper = data.type === 'browse' ? BrowseScreenWrapper : CollectionScreenWrapper
  const menuBranding = getMenuBranding(data.content)

  return (
    <Box data-testid="menu-experience-page">
      {isCategoryNavVisible && (
        <Box bg={Color.White} zIndex={ZIndex.Sticky} style={{ position: 'sticky', top: 0 }}>
          <MenuExperienceCategoryNav
            links={links}
            pathname={location.pathname}
            query={location.query}
          />
        </Box>
      )}
      <MenuBrandingProvider value={menuBranding}>
        <MenuExperienceNavigationProvider value={navigationActions}>
          <MenuBrandingContext.Consumer>
            {({ backgroundColor, backgroundImageUrl, textColor }) => (
              <Box
                data-testid="menu-experience-page-content"
                display={Display.Flex}
                flexDirection={FlexDirection.Column}
                maxWidth="1600px"
                paddingV={4}
                style={{
                  margin: '0 auto',
                  ...(backgroundColor ? { backgroundColor } : {}),
                  ...(textColor ? { color: textColor } : {}),
                  ...(backgroundImageUrl
                    ? {
                        backgroundImage: `url(${backgroundImageUrl})`,
                        backgroundPosition: 'top center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '100% auto',
                      }
                    : {}),
                }}
              >
                <Box paddingH={[3, 3, 6]}>
                  <ScreenWrapper contentBlocks={data.content} />
                </Box>
              </Box>
            )}
          </MenuBrandingContext.Consumer>
        </MenuExperienceNavigationProvider>
      </MenuBrandingProvider>
    </Box>
  )
}
